/** @format */

import { Box, Container, Link, styled, Typography } from "@mui/material";
import React from "react";
import { Footer } from "../allComponents/AllComponents";

const PageContainer = styled(Container)(({ theme }) => ({
	paddingTop: theme.spacing(8),
	paddingBottom: theme.spacing(8),
	fontFamily: "'Inter', sans-serif",
}));

const Section = styled(Box)(({ theme }) => ({
	marginBottom: theme.spacing(4),
}));

const Disclosure = () => {
	return (
		<React.Fragment>
			<PageContainer maxWidth="md">
				<Typography
					variant="h3"
					gutterBottom
					sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}>
					Money Transmitter Disclosure
				</Typography>
				<Typography variant="subtitle2" gutterBottom>
					Last updated: July 18, 2026
				</Typography>

				<Section>
					<Typography variant="h5" gutterBottom>
						Scope of This Disclosure
					</Typography>
					<Typography variant="body1" paragraph>
						This Money Transmitter Disclosure applies to <b>FundsyFi Circle</b>,
						our group savings and rotating-contribution product, where authorized
						contributions and payouts are processed through Stripe.{" "}
						<b>FundsyFi Recover</b>, our accounts-receivable and revenue-recovery
						product for businesses, is separate and is governed by its own terms.
						FundsyFi Recover does <b>not</b> take custody of, hold, or escrow your
						or your customers’ funds; payments on your invoices are made through
						your own QuickBooks and payment arrangements.
					</Typography>
				</Section>

				<Section>
					<Typography variant="h5" gutterBottom>
						1. Regulatory Status
					</Typography>
					<Typography variant="body1" paragraph>
						FundsyFi itself does <b>not</b> hold or transmit customer funds. All
						contributions and payouts are processed and escrowed by Stripe,
						Inc., a registered Money Services Business (MSB) with FinCEN
						(Registration No. 31000100522337) and licensed as a money
						transmitter in multiple U.S. states.
					</Typography>
				</Section>

				<Section>
					<Typography variant="h5" gutterBottom>
						2. How Your Funds Are Handled
					</Typography>
					<Box component="ul" sx={{ pl: 2 }}>
						<li>
							<Typography variant="body1">
								When you join a Circle, your authorized contributions are
								transmitted to and held in escrow by Stripe Connect under its
								money-transmitter license.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								FundsyFi never takes custody of your money; it only initiates
								payment instructions through Stripe.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								On your scheduled payout date, Stripe distributes the full pot
								directly to your bank account.
							</Typography>
						</li>
					</Box>
				</Section>

				<Section>
					<Typography variant="h5" gutterBottom>
						3. Consumer Protections
					</Typography>
					<Box component="ul" sx={{ pl: 2 }}>
						<li>
							<Typography variant="body1">
								Escrow Safeguard: Funds are kept in a dedicated escrow account,
								separate from operating accounts.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								Record Keeping: All transactions are logged and auditable in
								real time via your FundsyFi dashboard.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								Dispute Resolution: If a contribution fails or a payout is
								delayed, Stripe’s dispute process and FundsyFi support team will
								resolve the issue.
							</Typography>
						</li>
					</Box>
				</Section>

				<Section>
					<Typography variant="h5" gutterBottom>
						4. Fees & Payouts
					</Typography>
					<Box component="ul" sx={{ pl: 2 }}>
						<li>
							<Typography variant="body1">
								FundsyFi is free to use — there are no subscriptions or
								recurring charges. You only pay fees when transacting.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>Contribution Fees:</b> Each contribution incurs a Stripe
								processing fee of 2.9% + $0.30, added on top of your
								contribution. FundsyFi charges $0.00 on contributions.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>Payout Fees:</b> When you receive your payout, a 2.5%
								FundsyFi platform fee is deducted from the payout amount.
								Standard payouts (2–3 business days) have no additional Stripe
								transfer fee.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>Instant Payout Fees:</b> If you choose instant payout
								(received in minutes), Stripe charges 1% of the payout amount
								(minimum $0.50), deducted from your payout.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>Late Contribution Fees:</b> If you miss a contribution
								deadline, you have a 24-hour grace period to pay without
								penalty. After that, a 2.5% late fee applies to each overdue
								contribution and is added to the contribution total.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								All deductions are handled by Stripe, and you will see both
								gross and net amounts in your transaction details. For full fee
								details, see our <Link href="/fees">Fees</Link> page.
							</Typography>
						</li>
					</Box>
				</Section>

				<Section>
					<Typography variant="h5" gutterBottom>
						5. Privacy & Compliance
					</Typography>
					<Typography variant="body1" paragraph>
						All personally identifiable information and payment details are
						handled according to our{" "}
						<Link href="/privacy-policy">Privacy Policy</Link> and comply with:
					</Typography>
					<Box component="ul" sx={{ pl: 2 }}>
						<li>
							<Typography variant="body1">
								Gramm-Leach-Bliley Act (GLBA)
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								Bank Secrecy Act (BSA) / Anti-Money-Laundering (AML) regulations
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								PCI-DSS standards for cardholder data security
							</Typography>
						</li>
					</Box>
				</Section>

				<Section>
					<Typography variant="h5" gutterBottom>
						6. Contact for Regulatory Inquiries
					</Typography>
					<Typography variant="body1" paragraph>
						For questions about fund handling, Stripe’s MSB licensing, or this
						disclosure, contact:
					</Typography>
					<Typography variant="body1">
						<Link href="mailto:fundsyfi@fundsyfi.com">
							fundsyfi@fundsyfi.com
						</Link>
					</Typography>
					{/* <Typography variant="body1">+1 443-379-3232</Typography> */}
					<Typography variant="body1">Houston, Texas 77085</Typography>
				</Section>
			</PageContainer>
			<Footer />
		</React.Fragment>
	);
};

export default Disclosure;
